import { stringify } from 'query-string'
import { fetchUtils } from 'ra-core'
import { addDateRangeToQuery } from './helpers/index'
import authProvider from './authProvider'
import { ops, vendorOps } from './Transactions/TransactionList'

export const apiUrl = process.env.REACT_APP_ADMIN_API

export const httpClient = (url, options = {}) => {
  // Skip API calls to /users/me and /2fa/clear-verification during 2FA verification
  const currentPath = window.location.pathname
  if (
    (currentPath === '/verify-2fa' || currentPath === '/setup-2fa') &&
    (url.includes('/users/me') || url.includes('/2fa/clear-verification'))
  ) {
    // Return a rejected promise with a custom error
    return Promise.reject(
      new Error('Skipping API call during 2FA verification')
    )
  }

  const noptions = options
  // Ensure cookies (e.g., trusted-device) are sent with requests
  if (!noptions.credentials) {
    noptions.credentials = 'include'
  }

  const actingAs = JSON.parse(
    localStorage.getItem('RaStore.organisation.actingAs')
  )

  if (actingAs && actingAs.organisationId !== 0) {
    const addr = new URL(url)
    const params = addr.searchParams

    params.delete('organisation_id')
    params.set('organisation_id', actingAs.organisationId)

    url = addr.toString()
  }

  if (!noptions.headers) {
    noptions.headers = new Headers({ Accept: 'application/json' })
  }

  return authProvider.getAccessToken().then((token) => {
    if (token) {
      noptions.headers.set('Authorization', `Bearer ${token}`)
    }
    return fetchUtils.fetchJson(url, noptions).catch((error) => {
      // Check for 2FA required response
      if (
        error.status === 403 &&
        error.body &&
        error.body.detail &&
        typeof error.body.detail === 'object'
      ) {
        if (error.body.detail.code === '2FA_VERIFICATION_REQUIRED') {
          // Redirect to 2FA verification page
          window.location.href = '/verify-2fa'

          // Create a custom error with a readable message
          const customError = new Error('2FA verification required')
          customError.status = error.status
          customError.data = error.body.detail

          return Promise.reject(customError)
        }
      }

      // Extract the detailed error message from the response if available
      if (error.body && error.body.detail) {
        // Check if detail is an object (for 422 errors that return object data)
        const isObject =
          typeof error.body.detail === 'object' && error.body.detail !== null

        // Create a custom error with a readable message
        const errorMessage = isObject ? 'Validation error' : error.body.detail
        const customError = new Error(errorMessage)

        // Attach the original error details and status
        customError.status = error.status
        customError.data = isObject ? error.body.detail : null

        return Promise.reject(customError)
      }
      return Promise.reject(error)
    })
  })
}

const fileHttpClient = (url, options = {}) => {
  // Skip API calls to /users/me and /2fa/clear-verification during 2FA verification
  const currentPath = window.location.pathname
  if (
    (currentPath === '/verify-2fa' || currentPath === '/setup-2fa') &&
    (url.includes('/users/me') || url.includes('/2fa/clear-verification'))
  ) {
    // Return a rejected promise with a custom error
    return Promise.reject(
      new Error('Skipping API call during 2FA verification')
    )
  }

  const noptions = options
  // Ensure cookies (e.g., trusted-device) are sent with requests
  if (!noptions.credentials) {
    noptions.credentials = 'include'
  }

  if (!noptions.headers) {
    noptions.headers = new Headers()
  }

  return authProvider.getAccessToken().then((token) => {
    if (token) {
      noptions.headers.set('Authorization', `Bearer ${token}`)
    }
    return fetchUtils.fetchJson(url, noptions).catch((error) => {
      // Check for 2FA required response
      if (
        error.status === 403 &&
        error.body &&
        error.body.detail &&
        typeof error.body.detail === 'object'
      ) {
        if (error.body.detail.code === '2FA_VERIFICATION_REQUIRED') {
          // Redirect to 2FA verification page
          window.location.href = '/verify-2fa'

          // Create a custom error with a readable message
          const customError = new Error('2FA verification required')
          customError.status = error.status
          customError.data = error.body.detail

          return Promise.reject(customError)
        }
      }

      // Extract the detailed error message from the response if available
      if (error.body && error.body.detail) {
        // Check if detail is an object (for 422 errors that return object data)
        const isObject =
          typeof error.body.detail === 'object' && error.body.detail !== null

        // Create a custom error with a readable message
        const errorMessage = isObject ? 'Validation error' : error.body.detail
        const customError = new Error(errorMessage)

        // Attach the original error details and status
        customError.status = error.status
        customError.data = isObject ? error.body.detail : null

        return Promise.reject(customError)
      }
      return Promise.reject(error)
    })
  })
}

const dataProvider = {
  getList: (resource, params) => {
    const { page, perPage } = params.pagination
    const { field, order } = params.sort
    const offset = (page - 1) * perPage
    const query = {
      sort_field: field,
      sort_dir: order,
      offset: JSON.stringify(offset < 0 ? 0 : offset),
      limit: JSON.stringify(perPage),
      ...params.filter,
    }
    if (params.filter && params.filter.startDate && params.filter.endDate) {
      const newQuery = addDateRangeToQuery(
        params.filter.query,
        params.filter.startDate,
        params.filter.endDate
      )
      query.query = JSON.stringify(newQuery)
    }

    const url = `${apiUrl}/${resource}?${stringify(query)}`

    return httpClient(url).then(({ headers, json }) => {
      return {
        data: json,
        total: parseInt(headers.get('content-range').split('/').pop(), 10),
      }
    })
  },

  getOne: (resource, params) => {
    return httpClient(`${apiUrl}/${resource}/${params.id}`).then(
      ({ json }) => ({
        data: json,
      })
    )
  },

  getMany: (resource, params) => {
    // React-Admin provides params.ids (array of ids).
    // Our backend expects a single 'ids' query param containing a comma-separated list (e.g., ids=1,2,3).
    const toArray = (val) => {
      if (Array.isArray(val)) return val
      if (typeof val === 'string') {
        return val
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
      }
      return []
    }

    const ids = [
      ...toArray(params?.ids),
      // Fallbacks for some custom callers that pass ids in 'filter'
      ...toArray(params?.filter),
    ]

    const query = {}
    if (ids.length > 0) {
      // Deduplicate while preserving order
      const seen = new Set()
      const deduped = ids.filter((id) => {
        const key = String(id)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      query.ids = deduped.join(',')
    }

    const url = `${apiUrl}/${resource}?${stringify(query)}`
    return httpClient(url).then(({ json }) => ({ data: json }))
  },

  getManyReference: (resource, oparams) => {
    const params = oparams

    const { page, perPage } = params.pagination
    const { field, order } = params.sort

    if (typeof params.target !== 'undefined') {
      params.filter[params.target] = params.id
    }

    const query = {
      sort_field: field,
      sort_dir: order,
      offset: JSON.stringify((page - 1) * perPage),
      limit: JSON.stringify(perPage),
      ...params.filter,
    }

    const url = `${apiUrl}/${resource}?${stringify(query)}`

    return httpClient(url).then(({ headers, json }) => ({
      data: json,
      total: parseInt(headers.get('content-range').split('/').pop(), 10),
    }))
  },

  update: (resource, params) => {
    if (resource === 'vendors_tags') {
      resource = 'vendors/tags'
    }

    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }))
  },

  getProfile: () => {
    // Skip the API call if we're in the 2FA verification flow
    const currentPath = window.location.pathname
    if (currentPath === '/verify-2fa' || currentPath === '/setup-2fa') {
      // Return minimal profile data during 2FA verification
      return Promise.resolve({ data: { id: 'temp', name: 'Verifying 2FA' } })
    }

    return httpClient(`${apiUrl}/users/me`, {
      method: 'GET',
    }).then(({ json }) => ({ data: json }))
  },

  updateProfile: (params) => {
    return httpClient(`${apiUrl}/users/me`, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }))
  },

  updateMany: (resource, params) => {
    const query = {}

    if (params.filter.length > 0) {
      query.ids = JSON.stringify(params.filter)
    }

    return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }))
  },

  create: (resource, params) => {
    let url = `${apiUrl}/${resource}`

    if (
      resource === 'import_logs' &&
      params.data.file &&
      params.data.file.rawFile
    ) {
      const formData = new FormData()
      formData.append('file', params.data.file.rawFile)

      return fileHttpClient(url, {
        method: 'POST',
        body: formData,
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }))
    }
    if (resource === 'import_logs' && !params.data.file) {
      resource = 'import_logs_manual'
      url = `${apiUrl}/${resource}`
    }

    return httpClient(url, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id },
    }))
  },

  delete: (resource, params) => {
    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'DELETE',
    }).then(({ json }) => ({ data: json }))
  },
  deleteMany: async (resource, params) => {
    // For vendors, we need to delete one by one since the backend doesn't support bulk deletion
    if (resource === 'vendors') {
      try {
        const deletePromises = params.ids.map((id) =>
          httpClient(`${apiUrl}/${resource}/${id}`, {
            method: 'DELETE',
          }).then(({ json }) => json)
        )

        const results = await Promise.all(deletePromises)
        return { data: results }
      } catch (error) {
        console.error(`Error deleting ${resource}:`, error)
        return Promise.reject(error)
      }
    }

    // For other resources, use the standard approach
    const query = {}

    if (params.ids.length > 0) {
      query.ids = params.ids.join(',')
    }

    return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: 'DELETE',
    }).then(({ json }) => ({ data: json }))
  },

  uploadFile: (resource, params) => {
    const formData = new FormData()

    // Append the file with the correct field name
    // Always use 'import_file' field name for file uploads
    formData.append('import_file', params.import_file.rawFile)

    // Handle organisation context
    const actingAs = JSON.parse(
      localStorage.getItem('RaStore.organisation.actingAs')
    )
    if (actingAs && actingAs.organisationId !== 0) {
      params.organisation_id = actingAs.organisationId
    }

    // Append all other parameters to the FormData
    Object.keys(params).forEach((key) => {
      if (key !== 'import_file') {
        formData.append(key, params[key])
      }
    })

    return httpClient(`${apiUrl}/${resource}`, {
      method: 'POST',
      body: formData,
    }).then(({ json }) => {
      // For Excel preview, return the entire JSON response
      if (resource === 'vendors/preview-excel') {
        return {
          data: json,
          id: json.id,
          message: json.message,
        }
      }
      // For other endpoints, return the ID or filename
      return {
        data: json.id || json.filename,
        id: json.id,
        message: json.message,
      }
    })
  },

  transactionChangeVendor: (params) => {
    return httpClient(`${apiUrl}/transactions/changeVendor/${params.id}`, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }))
  },

  assignGroup: (params) => {
    return httpClient(`${apiUrl}/assign-group`, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }))
  },

  downloadFileImport: (id) => {
    return httpClient(`${apiUrl}/import_logs/${id}/download`, {
      method: 'POST',
    }).then(({ json }) => ({ data: json }))
  },

  startFileImport: (id) => {
    return httpClient(`${apiUrl}/import_logs/${id}/start_process`, {
      method: 'POST',
    }).then(({ json }) => ({ data: json }))
  },

  addReferenceVendor: (params) => {
    return httpClient(`${apiUrl}/referenceVendorMapper`, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }))
  },

  importDemoData: () => {
    return httpClient(`${apiUrl}/organisations/import/demo`, {
      method: 'GET',
    }).then(({ json }) => ({ data: json }))
  },

  getDemoCreditScore: () => {
    return httpClient(`${apiUrl}/organisations/import/credit`, {
      method: 'GET',
    }).then(({ json }) => ({ data: json }))
  },

  transactionConfirmVendor: (params) => {
    const query = {
      organisation_id: params.organisation_id,
    }

    if (params.ids.length > 0) {
      query.ids = params.ids
    }

    return httpClient(
      `${apiUrl}/transactions/confirmVendor/?${stringify(query)}`,
      {
        method: 'PUT',
        body: JSON.stringify(params.data),
      }
    ).then(({ json }) => ({ data: json }))
  },

  getDateRange: async () => {
    const url = `${apiUrl}/charts/dateRange`

    const { json } = await httpClient(url)

    return { data: json }
  },

  getFeatures: async () => {
    const url = `${apiUrl}/features`

    const { json } = await httpClient(url)

    return { data: json }
  },
  getChart: async (chartType, params) => {
    if (params.startDate && params.endDate) {
      const newQuery = addDateRangeToQuery(
        params.query,
        params.startDate,
        params.endDate
      )
      params.query = JSON.stringify(newQuery)
    }
    const query = {
      ...params,
    }

    const url = `${apiUrl}/charts/${chartType}?${stringify(query)}`

    let { json } = await httpClient(url)
    if (chartType === 'country') {
      json = json.map((item) => {
        item.value /= 100
        return item
      })
    }
    return { data: json }
  },

  getLevels: () => {
    const url = `${apiUrl}/tag_categories/levels`

    return httpClient(url).then(({ json }) => ({
      data: json,
    }))
  },

  getPriceTrackingTags: (params) => {
    const query = {
      ...params.filter,
    }
    const url = `${apiUrl}/priceTrackingTags?${stringify(query)}`

    return httpClient(url).then(({ json }) => ({
      data: json,
    }))
  },

  getTagCategories: () => {
    const url = `${apiUrl}/tag_categories`

    return httpClient(url).then(({ json }) => ({
      data: json,
    }))
  },

  getTagsByCategory: (category) => {
    const url = `${apiUrl}/tags?tag_category_id=${category}`

    return httpClient(url).then(({ json }) => ({
      data: json,
    }))
  },

  getTagsForCategory: async (tagCategoryId) => {
    const url = `${apiUrl}/tags?limit=1000&offset=0&sort_dir=ASC&sort_field=name&tag_category_id=${tagCategoryId}`
    const { json } = await httpClient(url)
    return json
  },

  getFilterFields: () => {
    const url = `${apiUrl}/tag_categories?sort_field=name`
    return httpClient(url).then(({ json }) => {
      json.map((tc) => {
        tc.label = tc.name
        tc.name = `tag_id[${tc.id}]`
        tc.operators = ops
        tc.datatype = `tag${tc.id}`
        tc.id = `tag_id_${tc.id}`
        return tc
      })
      json.push({
        label: 'Vendor',
        name: 'vendor_id',
        id: 'vendor_id',
        operators: vendorOps,
        datatype: 'vendor',
      })
      json.push({
        label: 'Group',
        name: 'group_id',
        id: 'group_id',
        operators: vendorOps,
        datatype: 'group',
      })
      // json.push({
      //   label: 'Country',
      //   name: 'territory_id',
      //   id: 'territory_id',
      //   operators: ops,
      //   datatype: 'territory',
      // })

      // json.push({
      //   label: 'Invoice Date',
      //   name: 'invoice_date',
      //   operators: [{ name: 'between', label: 'is between' }],
      //   datatype: 'dateRange',
      // })
      json.sort((a, b) => {
        if (a.label < b.label) {
          return -1
        }
        if (a.label > b.label) {
          return 1
        }
        return 0
      })
      return { data: json }
    })
  },

  getCSVRowCount: (params) => {
    const query = {
      ...params,
    }

    // Handle date range parameters in the same way as getList
    if (params && params.startDate && params.endDate) {
      const newQuery = addDateRangeToQuery(
        params.query,
        params.startDate,
        params.endDate
      )
      query.query = JSON.stringify(newQuery)
    }

    const url = `${apiUrl}/exports/csv_row_count?${stringify(query)}`

    return httpClient(url).then(({ json }) => ({
      data: json,
    }))
  },

  getCSVUrl: (params, token, format = 'csv') => {
    const query = {
      token,
      format,
      ...params,
    }

    // Handle date range parameters in the same way as getCSVRowCount and getList
    if (params && params.startDate && params.endDate) {
      const newQuery = addDateRangeToQuery(
        params.query,
        params.startDate,
        params.endDate
      )
      query.query = JSON.stringify(newQuery)
    }

    const url = `${apiUrl}/exports/csv?${stringify(query)}`

    return new Promise((resolve) => {
      resolve(url)
    })
  },
  forgottenPassword: (params) => {
    return httpClient(`${apiUrl}/auth/forgot-password`, {
      method: 'POST',
      body: JSON.stringify(params),
    }).then(({ json }) => ({ data: json }))
  },
  resetPassword: (params) => {
    // this method is used for forgotten passwords
    // IMPORTANT: Use a bare fetch without Authorization header or persisted credentials.
    const url = `${apiUrl}/auth/reset-password2`
    const options = {
      method: 'POST',
      credentials: 'omit', // do not send cookies on reset
      headers: new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(params),
    }
    return fetchUtils
      .fetchJson(url, options)
      .then(({ json }) => ({ data: json }))
  },
  validateInviteToken: (token) => {
    const url = `${apiUrl}/auth/invite-validate?token=${encodeURIComponent(
      token
    )}`
    return fetchUtils
      .fetchJson(url, { method: 'GET', credentials: 'omit' })
      .then(({ json }) => ({ data: json }))
  },
  validateResetToken: (token) => {
    const url = `${apiUrl}/auth/reset-validate?token=${encodeURIComponent(
      token
    )}`
    return fetchUtils
      .fetchJson(url, { method: 'GET', credentials: 'omit' })
      .then(({ json }) => ({ data: json }))
  },
  resetPasswordInvite: (params) => {
    // this method is used for first-time invitation password set
    const url = `${apiUrl}/auth/invite-reset`
    const options = {
      method: 'POST',
      credentials: 'omit',
      headers: new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(params),
    }
    return fetchUtils
      .fetchJson(url, options)
      .then(({ json }) => ({ data: json }))
  },
  updatePassword: (id, params) => {
    // this method is used for a logged-in user
    return httpClient(`${apiUrl}/users/password/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    }).then(({ json }) => ({ data: json }))
  },

  /*
    Get the details of a widget
   */
  getWidget: (id) => {
    return httpClient(`${apiUrl}/widget/${id}`).then(({ json }) => {
      return json
    })
  },
  getWidgets: async () => {
    return httpClient(`${apiUrl}/widgets`).then(({ json }) => {
      return json
    })
  },
  getUserWidgets: async () => {
    return httpClient(`${apiUrl}/user-widgets`).then(({ json }) => {
      return json
    })
  },
  setUserWidgets: (widgets) => {
    return httpClient(`${apiUrl}/user-widgets`, {
      method: 'POST',
      body: JSON.stringify(widgets),
    }).then(({ json }) => {
      return json
    })
  },
  getUserAvailableWidgets: async () => {
    return httpClient(`${apiUrl}/user-available-widgets`).then(({ json }) => {
      return json
    })
  },
  /*
   add a new widget
   name: new name for the widget
   description: new description for the widget
   is_available: if set to true it will show as available for the user / system
   is_custom: if is_custom, only this user can see it as available
   custom_value: JSON representation of stored values needed by the APP to request data from the backend
                 and display it how the user wants
  */
  addWidget: (name, description, isAvailable, isCustom, customValue) => {
    return httpClient(`${apiUrl}/widget`, {
      method: 'POST',
      body: JSON.stringify({
        name,
        description,
        is_available: isAvailable,
        is_custom: isCustom,
        custom_value: JSON.stringify(customValue),
      }),
    }).then(({ json }) => {
      return json
    })
  },
  /*
   update a currently existing widget
   can be used to update a custom widget for a user or to update the system widgets
   id: widget ID to update
   name: new name for the widget
   description: new description for the widget
   is_available: if set to true it will show as available for the user / system
   is_custom: if is_custom, only this user can see it as available
   custom_value: JSON representation of stored values needed by the APP to request data from the backend
                 and display it how the user wants
   */
  updateWidget: (id, name, description, isAvailable, isCustom, customValue) => {
    return httpClient(`${apiUrl}/widget`, {
      method: 'PUT',
      body: JSON.stringify({
        id,
        name,
        description,
        is_available: isAvailable,
        is_custom: isCustom,
        custom_value: JSON.stringify(customValue),
      }),
    }).then(({ json }) => {
      return json
    })
  },
  // completely delete a widget from the system
  removeWidget: (id) => {
    return httpClient(`${apiUrl}/widget/${id}`, {
      method: 'DELETE',
    }).then(({ json }) => {
      return json
    })
  },

  getPriceDashboard: async (tagId, params) => {
    const query = {
      ...params.filter,
    }
    const url = `${apiUrl}/priceDashboard?tag_id=${tagId}&${stringify(query)}`

    const { json } = await httpClient(url)

    return { data: json }
  },

  custom: async (resource, params) => {
    const { method = 'GET', body, ...rest } = params
    const url = `${apiUrl}/${resource}`

    try {
      const { json } = await httpClient(url, {
        method,
        body,
        ...rest,
      })

      return { data: json }
    } catch (error) {
      console.error('Error in custom request:', error)
      return Promise.reject(error)
    }
  },

  check2FAStatus: async () => {
    // Skip the actual API call if we're already on a 2FA page
    const currentPath = window.location.pathname
    if (currentPath === '/verify-2fa') {
      return { verified: false, redirect: '/verify-2fa' }
    }

    try {
      // Ask backend if this device/session requires 2FA verification
      const { json } = await httpClient(`${apiUrl}/2fa/status`)
      const requires = !!json?.requires_verification
      const redirect = json?.redirect
      return { verified: !requires, redirect }
    } catch (error) {
      // If we get a 2FA verification required error, return false
      if (error.message === '2FA verification required') {
        return { verified: false, redirect: '/verify-2fa' }
      }
      // If we get a 2FA setup required error, return false with setup redirect
      if (error.message === '2FA setup required') {
        return { verified: false, redirect: '/setup-2fa' }
      }
      // For other errors, continue (don't lock users out if status fails)
      return { verified: true }
    }
  },

  getImportStatus: (importLogId) => {
    return httpClient(`${apiUrl}/import_logs/${importLogId}`).then(
      ({ json }) => ({
        data: json,
      })
    )
  },
}

export default dataProvider
